import {
  Box,
  Button,
  Chip,
  Container,
  Grid,
  Paper,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { Pagination } from "@material-ui/lab";
import { makeStyles } from "@material-ui/styles";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import BookmarkIcon from "@material-ui/icons/Bookmark";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CommentIcon from "@material-ui/icons/Comment";
import MenuBookIcon from "@material-ui/icons/MenuBook";
import PlayCircleFilledIcon from "@material-ui/icons/PlayCircleFilled";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import { useAppDispatch, useAppSelector } from "app/hooks";
import LOADING from "assets/img/loading.gif";
import queryString from "query-string";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  fetchListLessonByUser,
  selectFilterLessonListByUser,
  selectLessonListByUser,
  selectLoadingLessonListByUser,
  setFilterListLessonByUser,
} from "./listLessonSlice";

export default function ListLesson() {
  const useStyles = makeStyles((theme: Theme) => ({
    container: {
      paddingTop: 28,
      paddingBottom: 48,
      maxWidth: 1280,
      margin: "0 auto",
      [theme.breakpoints?.down("sm")]: {
        paddingTop: 16,
        paddingBottom: 24,
      },
    },
    backButton: {
      marginBottom: 20,
      color: "#475569",
      fontWeight: 700,
      fontSize: "0.875rem",
      textTransform: "none",
      borderRadius: 10,
      padding: "6px 14px",
      backgroundColor: "#F8FAFC",
      border: "1px solid #E2E8F0",
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: "#F1F5F9",
        color: "#0F172A",
        transform: "translateX(-2px)",
      },
    },
    headerBanner: {
      background: "linear-gradient(135deg, #0F172A 0%, #1E1B4B 50%, #312E81 100%)",
      borderRadius: 24,
      padding: "32px 36px",
      color: "#FFFFFF",
      marginBottom: 28,
      position: "relative",
      overflow: "hidden",
      boxShadow: "0 12px 32px rgba(15, 23, 42, 0.25)",
      [theme.breakpoints?.down("sm")]: {
        padding: "20px 18px",
        borderRadius: 16,
      },
    },
    headerGlow: {
      position: "absolute",
      top: "-40%",
      right: "-10%",
      width: 350,
      height: 350,
      background: "radial-gradient(circle, rgba(99, 102, 241, 0.3) 0%, rgba(99, 102, 241, 0) 70%)",
      pointerEvents: "none",
    },
    headerBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      backgroundColor: "rgba(255, 255, 255, 0.12)",
      backdropFilter: "blur(8px)",
      padding: "6px 16px",
      borderRadius: 20,
      fontSize: "0.8rem",
      fontWeight: 800,
      color: "#FDE047",
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      marginBottom: 12,
      border: "1px solid rgba(253, 224, 71, 0.3)",
    },
    headerTitle: {
      fontSize: "1.85rem",
      fontWeight: 900,
      color: "#FFFFFF",
      marginBottom: 8,
      [theme.breakpoints?.down("sm")]: {
        fontSize: "1.3rem",
      },
    },
    headerSubtitle: {
      fontSize: "0.95rem",
      color: "#CBD5E1",
      fontWeight: 500,
    },

    // Filter Chips
    filterRow: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      marginBottom: 24,
      flexWrap: "wrap",
    },
    filterChip: {
      fontWeight: 800,
      fontSize: "0.825rem",
      borderRadius: 20,
      padding: "6px 16px",
      cursor: "pointer",
      transition: "all 0.2s ease",
    },
    filterChipActive: {
      backgroundColor: "#4F46E5",
      color: "#FFFFFF",
      boxShadow: "0 4px 14px rgba(79, 70, 229, 0.3)",
    },
    filterChipInactive: {
      backgroundColor: "#FFFFFF",
      color: "#64748B",
      border: "1px solid #E2E8F0",
      "&:hover": {
        backgroundColor: "#F8FAFC",
        color: "#0F172A",
      },
    },

    // Lesson Card
    lessonCard: {
      backgroundColor: "#FFFFFF",
      borderRadius: 20,
      border: "1px solid #E2E8F0",
      boxShadow: "0 6px 20px rgba(0, 0, 0, 0.04)",
      overflow: "hidden",
      marginBottom: 20,
      transition: "all 0.25s ease",
      "&:hover": {
        transform: "translateY(-3px)",
        boxShadow: "0 12px 30px rgba(0, 0, 0, 0.08)",
        borderColor: "#C7D2FE",
      },
    },
    thumbContainer: {
      position: "relative",
      width: "100%",
      height: "100%",
      minHeight: 180,
      backgroundColor: "#0F172A",
      overflow: "hidden",
      cursor: "pointer",
    },
    thumbImage: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      transition: "transform 0.3s ease",
      "&:hover": {
        transform: "scale(1.05)",
      },
    },
    playOverlay: {
      position: "absolute",
      inset: 0,
      background: "rgba(15, 23, 42, 0.35)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#FFFFFF",
      transition: "background 0.2s ease",
      "&:hover": {
        background: "rgba(15, 23, 42, 0.2)",
      },
    },
    categoryOverlay: {
      position: "absolute",
      top: 12,
      left: 12,
      backgroundColor: "rgba(15, 23, 42, 0.75)",
      backdropFilter: "blur(8px)",
      color: "#818CF8",
      fontWeight: 800,
      fontSize: "0.725rem",
      padding: "4px 10px",
      borderRadius: 8,
      textTransform: "uppercase",
      letterSpacing: "0.04em",
      border: "1px solid rgba(255, 255, 255, 0.15)",
    },

    cardBody: {
      padding: 24,
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      height: "100%",
      [theme.breakpoints?.down("sm")]: {
        padding: 16,
      },
    },
    cardMetaRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 10,
    },
    dateText: {
      fontSize: "0.8rem",
      fontWeight: 700,
      color: "#64748B",
      display: "flex",
      alignItems: "center",
      gap: 6,
    },
    lessonName: {
      fontSize: "1.15rem",
      fontWeight: 900,
      color: "#0F172A",
      lineHeight: 1.4,
      marginBottom: 8,
      cursor: "pointer",
      transition: "color 0.2s ease",
      "&:hover": {
        color: "#4F46E5",
      },
      [theme.breakpoints?.down("sm")]: {
        fontSize: "1rem",
      },
    },
    descriptionText: {
      fontSize: "0.875rem",
      color: "#475569",
      lineHeight: 1.5,
      marginBottom: 16,
    },
    cardFooter: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      paddingTop: 12,
      borderTop: "1px solid #F1F5F9",
    },
    commentCount: {
      fontSize: "0.8rem",
      fontWeight: 700,
      color: "#64748B",
      display: "flex",
      alignItems: "center",
      gap: 6,
    },
    btnStudy: {
      background: "linear-gradient(135deg, #4F46E5 0%, #4338CA 100%)",
      color: "#FFFFFF",
      fontWeight: 800,
      fontSize: "0.825rem",
      textTransform: "none",
      borderRadius: 10,
      padding: "6px 18px",
      boxShadow: "0 4px 14px rgba(79, 70, 229, 0.3)",
      transition: "all 0.2s ease",
      "&:hover": {
        background: "linear-gradient(135deg, #4338CA 0%, #3730A3 100%)",
        boxShadow: "0 6px 18px rgba(79, 70, 229, 0.45)",
      },
    },

    loading: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: 300,
    },
  }));

  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const classes = useStyles();
  const params: any = queryString.parse(location.search);
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints?.down("sm"));

  const filter = useAppSelector(selectFilterLessonListByUser);
  const listLesson = useAppSelector(selectLessonListByUser);
  const loading = useAppSelector(selectLoadingLessonListByUser);

  const [activeTab, setActiveTab] = useState<"ALL" | "WATCHED" | "UNWATCHED">("ALL");

  useEffect(() => {
    dispatch(fetchListLessonByUser({ ...filter, month: params.month, year: params.year }));
  }, [dispatch, filter]);

  const handleChangePage = (page: number) => {
    dispatch(
      setFilterListLessonByUser({
        ...filter,
        page: page - 1,
      })
    );
  };

  const rawLessons: any[] = listLesson?.content || [];

  const displayLessons = rawLessons.filter((item: any) => {
    if (activeTab === "WATCHED") return item.watched;
    if (activeTab === "UNWATCHED") return !item.watched;
    return true;
  });

  const monthFormatted = String(params.month || "08").padStart(2, "0");
  const yearFormatted = params.year || "2026";

  return (
    <Container className={classes.container} maxWidth={false}>
      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIcon />}
        className={classes.backButton}
        onClick={() => navigate(-1)}
      >
        Quay lại
      </Button>

      {/* Header Banner */}
      <Box className={classes.headerBanner}>
        <Box className={classes.headerGlow} />
        <Box className={classes.headerBadge}>
          <CalendarTodayIcon style={{ fontSize: 16 }} />
          DANH SÁCH BÀI HỌC THÁNG {monthFormatted}/{yearFormatted}
        </Box>
        <Typography className={classes.headerTitle}>
          THƯ VIỆN BÀI HỌC HỌC TẬP GPET EDU
        </Typography>
        <Typography className={classes.headerSubtitle}>
          Khám phá các bài giảng video chuyên đề, tài liệu đào tạo và bài kiểm tra đánh giá chất lượng.
        </Typography>
      </Box>

      {/* Filter Tabs */}
      <Box className={classes.filterRow}>
        <Box
          className={`${classes.filterChip} ${activeTab === "ALL" ? classes.filterChipActive : classes.filterChipInactive}`}
          onClick={() => setActiveTab("ALL")}
        >
          <Box display="inline-flex" alignItems="center" style={{ gap: 6 }}>
            <MenuBookIcon style={{ fontSize: 18 }} />
            <span>TẤT CẢ BÀI HỌC ({rawLessons.length})</span>
          </Box>
        </Box>
        <Box
          className={`${classes.filterChip} ${activeTab === "UNWATCHED" ? classes.filterChipActive : classes.filterChipInactive}`}
          onClick={() => setActiveTab("UNWATCHED")}
        >
          <Box display="inline-flex" alignItems="center" style={{ gap: 6 }}>
            <VisibilityOffIcon style={{ fontSize: 18 }} />
            <span>CHƯA XEM ({rawLessons.filter((x: any) => !x.watched).length})</span>
          </Box>
        </Box>
        <Box
          className={`${classes.filterChip} ${activeTab === "WATCHED" ? classes.filterChipActive : classes.filterChipInactive}`}
          onClick={() => setActiveTab("WATCHED")}
        >
          <Box display="inline-flex" alignItems="center" style={{ gap: 6 }}>
            <CheckCircleIcon style={{ fontSize: 18 }} />
            <span>ĐÃ XEM ({rawLessons.filter((x: any) => x.watched).length})</span>
          </Box>
        </Box>
      </Box>

      {/* Lesson List */}
      {loading ? (
        <Box className={classes.loading}>
          <img src={LOADING} alt="LOADING" />
        </Box>
      ) : (
        <Box>
          {displayLessons.map((lesson: any) => {
            const pubDateStr = String(lesson?.lession?.publishDate || "20260801");
            const day = pubDateStr.slice(-2);
            const month = pubDateStr.slice(4, 6);
            const year = pubDateStr.slice(0, 4);

            const thumbUrl =
              lesson?.lession?.attachment?.urlThumbnail ||
              lesson?.lession?.powerPointFileList?.[0]?.powerPointFile?.urlThumbnail ||
              "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=600&auto=format&fit=crop&q=80";

            return (
              <Paper key={lesson.id} className={classes.lessonCard} elevation={0}>
                <Grid container spacing={0}>
                  {/* Left Column: Thumbnail */}
                  <Grid item xs={12} sm={4} md={3}>
                    <Box
                      className={classes.thumbContainer}
                      onClick={() =>
                        navigate({
                          pathname: `/lesson/${lesson.lessionId}`,
                          search: `year=${yearFormatted}&month=${monthFormatted}&attachmentID=${lesson?.lession?.attachmentID}`,
                        })
                      }
                    >
                      <img
                        src={thumbUrl}
                        alt="Lesson thumbnail"
                        className={classes.thumbImage}
                        onError={(e: any) => {
                          e.target.onerror = null;
                          e.target.src = "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=600&auto=format&fit=crop&q=80";
                        }}
                      />
                      <Box className={classes.categoryOverlay}>
                        {lesson?.lession?.lessionCategory?.name || "BÀI HỌC"}
                      </Box>
                      <Box className={classes.playOverlay}>
                        <PlayCircleFilledIcon style={{ fontSize: 54 }} />
                      </Box>
                    </Box>
                  </Grid>

                  {/* Right Column: Details */}
                  <Grid item xs={12} sm={8} md={9}>
                    <Box className={classes.cardBody}>
                      <Box>
                        <Box className={classes.cardMetaRow}>
                          <Typography className={classes.dateText}>
                            <CalendarTodayIcon style={{ fontSize: 15, color: "#64748B" }} />
                            Phát hành: {day}/{month}/{year}
                          </Typography>

                          {lesson?.watched ? (
                            <Chip
                              icon={<CheckCircleIcon style={{ color: "#16A34A" }} />}
                              label="Đã xem"
                              size="small"
                              style={{ backgroundColor: "#F0FDF4", color: "#16A34A", fontWeight: 800, fontSize: "0.75rem" }}
                            />
                          ) : (
                            <Chip
                              icon={<VisibilityIcon style={{ color: "#4F46E5" }} />}
                              label="Chưa xem"
                              size="small"
                              style={{ backgroundColor: "#EEF2FF", color: "#4F46E5", fontWeight: 800, fontSize: "0.75rem" }}
                            />
                          )}
                        </Box>

                        <Typography
                          className={classes.lessonName}
                          onClick={() =>
                            navigate({
                              pathname: `/lesson/${lesson.lessionId}`,
                              search: `year=${yearFormatted}&month=${monthFormatted}&attachmentID=${lesson?.lession?.attachmentID}`,
                            })
                          }
                        >
                          {lesson?.lession?.lessionName}
                        </Typography>

                        <Typography className={classes.descriptionText}>
                          {lesson?.lession?.lessionDescription?.length > 140
                            ? lesson?.lession?.lessionDescription.slice(0, 140) + "..."
                            : lesson?.lession?.lessionDescription}
                        </Typography>
                      </Box>

                      <Box className={classes.cardFooter}>
                        <Typography className={classes.commentCount}>
                          <CommentIcon style={{ fontSize: 16, color: "#64748B" }} />
                          {lesson?.lession?.noComments || 0} {t("home.detaillesson.binhluan")}
                        </Typography>

                        <Button
                          startIcon={<PlayCircleFilledIcon />}
                          className={classes.btnStudy}
                          onClick={() =>
                            navigate({
                              pathname: `/lesson/${lesson.lessionId}`,
                              search: `year=${yearFormatted}&month=${monthFormatted}&attachmentID=${lesson?.lession?.attachmentID}`,
                            })
                          }
                        >
                          Vào học ngay
                        </Button>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            );
          })}

          {/* Pagination */}
          {listLesson?.totalElements > listLesson?.size && (
            <Box mt={3} display="flex" justifyContent="center">
              <Pagination
                count={Math.ceil(listLesson.totalElements / listLesson.size)}
                variant="outlined"
                page={listLesson.number + 1}
                color="primary"
                shape="rounded"
                onChange={(e, page) => handleChangePage(page)}
              />
            </Box>
          )}
        </Box>
      )}
    </Container>
  );
}

